import { createContext, useContext, useReducer, ReactNode } from 'react';

export interface SimpleGameState {
  startArticle: string;
  goalArticle: string;
  currentArticle: string;
  path: string[];
  steps: number;
  backButtonsLeft: number;
  backButtonUsed: boolean;
  isGameActive: boolean;
  startTime: number;
  minSteps: number | undefined;
}

type GameAction = 
  | { type: 'START_GAME'; payload: { currentArticle: string; minSteps?: number } }
  | { type: 'START_TEST_GAME'; payload: { startArticle: string; goalArticle: string; minSteps?: number } }
  | { type: 'NAVIGATE_TO_ARTICLE'; payload: { article: string; steps: number } }
  | { type: 'USE_BACK_BUTTON'; payload: { previousArticle: string } }
  | { type: 'END_GAME' }
  | { type: 'RESET_GAME' };

const initialState: SimpleGameState = {
  startArticle: '',
  goalArticle: '', 
  currentArticle: '',
  path: [],
  steps: 0,
  backButtonsLeft: 1,
  backButtonUsed: false,
  isGameActive: false,
  startTime: 0,
  minSteps: undefined
};

function gameReducer(state: SimpleGameState, action: GameAction): SimpleGameState {
  switch (action.type) {
    case 'START_GAME':
      return {
        ...state,
        currentArticle: action.payload.currentArticle,
        path: [action.payload.currentArticle],
        steps: 0,
        isGameActive: true,
        startTime: Date.now(),
        backButtonsLeft: 1,
        backButtonUsed: false,
        minSteps: action.payload.minSteps
      };
    
    case 'START_TEST_GAME':
      return {
        ...state,
        startArticle: action.payload.startArticle,
        goalArticle: action.payload.goalArticle,
        currentArticle: action.payload.startArticle,
        path: [action.payload.startArticle],
        steps: 0,
        isGameActive: true,
        startTime: Date.now(),
        backButtonsLeft: 1,
        backButtonUsed: false,
        minSteps: action.payload.minSteps
      };
    
    case 'NAVIGATE_TO_ARTICLE':
      return {
        ...state,
        currentArticle: action.payload.article,
        path: [...state.path, action.payload.article],
        steps: action.payload.steps
      };
    
    case 'USE_BACK_BUTTON':
      const newPath = [...state.path];
      newPath.pop(); // Remove current article
      return {
        ...state,
        currentArticle: action.payload.previousArticle,
        path: newPath,
        backButtonsLeft: 0,
        backButtonUsed: true
      };
    
    case 'END_GAME':
      return {
        ...state,
        isGameActive: false
      };
    
    case 'RESET_GAME':
      return initialState;
    
    default:
      return state;
  }
}

export interface SimpleGameContextType {
  gameState: SimpleGameState;
  dispatch: React.Dispatch<GameAction>;
}

const SimpleGameContext = createContext<SimpleGameContextType | undefined>(undefined);

export function SimpleGameProvider({ children }: { children: ReactNode }) {
  const [gameState, dispatch] = useReducer(gameReducer, initialState);

  return (
    <SimpleGameContext.Provider value={{ gameState, dispatch }}>
      {children}
    </SimpleGameContext.Provider>
  );
}

export function useSimpleGame() {
  const context = useContext(SimpleGameContext);
  if (context === undefined) {
    throw new Error('useSimpleGame must be used within a SimpleGameProvider');
  }
  return context;
}